import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaEdit, FaPowerOff, FaTicketAlt, FaClock, FaUserPlus } from "react-icons/fa";
import Handler from "../../Helpers/Handler";
import { currencyFormatter, formatDate, formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton, DetailCard, FieldRow } from "../../Components/Needed";
import Timeline from "../../Components/Timeline";
import CouponFormModal from "./CouponFormModal";
import GrantModal from "./GrantModal";
import { listBackPath } from "../../Helpers/useListParams";

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "grants", label: "Grants" },
    { key: "redemptions", label: "Redemptions" },
];

const GRANT_STATUS_BADGE = {
    active: "text-bg-success",
    consumed: "text-bg-primary",
    revoked: "text-bg-danger",
    expired: "text-bg-secondary",
};

const REDEMPTION_STATUS_BADGE = {
    reserved: "text-bg-warning",
    committed: "text-bg-success",
    released: "text-bg-secondary",
};

const CouponView = () => {
    const { couponCode } = useParams();
    const navigate = useNavigate();

    const [coupon, setCoupon] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showGrantModal, setShowGrantModal] = useState(false);
    const [tab, setTab] = useState("overview");

    const [grants, setGrants] = useState({ list: [], total: 0 });
    const [grantsPage, setGrantsPage] = useState(1);
    const [grantsLoading, setGrantsLoading] = useState(false);

    const [redemptions, setRedemptions] = useState({ list: [], total: 0 });
    const [redemptionsPage, setRedemptionsPage] = useState(1);
    const [redemptionsLoading, setRedemptionsLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [detailResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/admin/coupon/${couponCode}` }),
            Handler({ method: "GET", url: `/admin/coupon/${couponCode}/log`, params: { limit: 20 } }),
        ]);
        setCoupon(detailResponse?.success ? detailResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [couponCode]);

    useEffect(() => { load(); }, [load]);

    const loadGrants = useCallback(async () => {
        setGrantsLoading(true);
        const response = await Handler({ method: "post", url: `/admin/coupon/${couponCode}/grants/${grantsPage - 1}`, params: { count: 10 } });
        setGrants(response?.success ? response?.data?.data || { list: [], total: 0 } : { list: [], total: 0 });
        setGrantsLoading(false);
    }, [couponCode, grantsPage]);

    useEffect(() => { if (tab === "grants") loadGrants(); }, [tab, loadGrants]);

    const loadRedemptions = useCallback(async () => {
        setRedemptionsLoading(true);
        const response = await Handler({ method: "post", url: `/admin/coupon/${couponCode}/redemptions/${redemptionsPage - 1}`, params: { count: 10 } });
        setRedemptions(response?.success ? response?.data?.data || { list: [], total: 0 } : { list: [], total: 0 });
        setRedemptionsLoading(false);
    }, [couponCode, redemptionsPage]);

    useEffect(() => { if (tab === "redemptions") loadRedemptions(); }, [tab, loadRedemptions]);

    const toggleStatus = async () => {
        if (!coupon) return;
        setTogglingStatus(true);
        const response = await Handler({ method: "patch", url: `/admin/coupon/${couponCode}/status`, data: { status: !coupon.status } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    const revokeGrant = async (grantId) => {
        const response = await Handler({ method: "patch", url: `/admin/coupon/grant/${grantId}/revoke`, data: {} });
        if (response?.success) {
            toast.success("Grant revoked");
            loadGrants();
        } else {
            toast.error(response?.data?.message || "Unable to revoke this grant");
        }
    };

    if (loading) {
        return (
            <div className="container-fluid py-3">
                <Skeleton height={20} width="180px" className="mb-4" />
                <div className="bg-white border rounded-4 p-4">
                    <Skeleton height={32} width="40%" className="mb-3" />
                    <Skeleton height={18} width="25%" className="mb-4" />
                    <Skeleton height={120} />
                </div>
            </div>
        );
    }

    if (!coupon) {
        return (
            <div className="container-fluid py-4">
                <div className="bg-white border rounded-4 text-center py-5 px-3">
                    <div className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: 64, height: 64 }}>
                        <FaTicketAlt className="text-muted" size={24} />
                    </div>
                    <h5 className="fw-semibold mb-2">Coupon not found</h5>
                    <p className="text-muted mb-4">This coupon doesn't exist or may have been removed.</p>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(listBackPath("/coupon"))}>
                        <FaArrowLeft className="me-2" /> Back to coupons
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container-fluid py-3">
                <div className="mb-3">
                    <button type="button" className="btn btn-link text-secondary text-decoration-none p-0 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/coupon"))}>
                        <FaArrowLeft size={13} />
                        <span>Back to coupons</span>
                    </button>
                </div>

                <div className="bg-white border rounded-4 shadow-sm mb-4 overflow-hidden">
                    <div className="p-4">
                        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
                            <div className="d-flex align-items-center gap-3">
                                <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 56, height: 56, background: "rgba(13, 110, 253, 0.08)" }}>
                                    <FaTicketAlt size={23} className="text-primary" />
                                </div>
                                <div>
                                    <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                        <h4 className="fw-semibold mb-0">{coupon.couponCode}</h4>
                                        <span className={`badge rounded-pill ${coupon.status ? "text-bg-success" : "text-bg-secondary"}`}>
                                            {coupon.status ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                    <div className="text-muted small">{coupon.name}</div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                {coupon.audience === "targeted" ? (
                                    <button type="button" className="btn btn-light border d-inline-flex align-items-center gap-2" onClick={() => setShowGrantModal(true)}>
                                        <FaUserPlus size={13} /> Grant to customers
                                    </button>
                                ) : ""}
                                <button type="button" className="btn btn-light border d-inline-flex align-items-center gap-2" onClick={toggleStatus} disabled={togglingStatus}>
                                    <FaPowerOff size={13} className={coupon.status ? "text-danger" : "text-success"} />
                                    {togglingStatus ? "Updating..." : coupon.status ? "Disable" : "Enable"}
                                </button>
                                <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={() => setShowEditModal(true)}>
                                    <FaEdit size={13} /> Edit coupon
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="border-top bg-light bg-opacity-50">
                        <div className="row g-0">
                            <div className="col-6 col-md-3">
                                <div className="px-4 py-3 border-end">
                                    <div className="text-muted small mb-1">Discount</div>
                                    <div className="fw-semibold">
                                        {coupon.discountType === "percentage" ? `${coupon.discountValue}% (cap ${currencyFormatter(coupon.maxDiscount || 0)})` : currencyFormatter(coupon.discountValue || 0)}
                                    </div>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="px-4 py-3 border-end">
                                    <div className="text-muted small mb-1">Redeemed</div>
                                    <div className="fw-semibold">{coupon.redeemedCount || 0}{coupon.usageLimit ? ` / ${coupon.usageLimit}` : " (unlimited)"}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="px-4 py-3 border-end">
                                    <div className="text-muted small mb-1">Audience</div>
                                    <div className="fw-semibold text-capitalize">{coupon.audience === "all" ? "Everyone" : "Targeted"}</div>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="px-4 py-3">
                                    <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                                        <FaClock size={12} /> Last updated
                                    </div>
                                    <div className="fw-medium small">{formatDateTime(coupon.updatedAt)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <ul className="nav nav-pills mb-3">
                    {TABS.map((t) => (
                        <li className="nav-item" key={t.key}>
                            <button type="button" className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>{t.label}</button>
                        </li>
                    ))}
                </ul>

                {tab === "overview" ? (
                    <div className="row g-4">
                        <div className="col-12 col-xl-7">
                            <DetailCard title="Description" icon={<FaTicketAlt />}>
                                <div className="bg-light rounded-3 p-3 text-secondary">{coupon.description || "-"}</div>
                            </DetailCard>

                            <DetailCard title="Coupon details">
                                <FieldRow label="Coupon code" value={coupon.couponCode} />
                                <FieldRow label="Name" value={coupon.name} />
                                <FieldRow label="Campaign" value={coupon.campaign} />
                                <FieldRow label="Applies to" value={(coupon.appliesTo || []).join(", ")} />
                                <FieldRow label="Per-customer limit" value={coupon.perCustomerLimit} />
                                <FieldRow label="Stackable with coins" value={coupon.stackableWithCoins ? "Yes" : "No"} />
                                <FieldRow label="Start date" value={coupon.startAt ? formatDate(coupon.startAt) : "Immediately"} />
                                <FieldRow label="Expiry date" value={coupon.expireAt ? formatDate(coupon.expireAt) : "No expiry"} />
                                <FieldRow label="Created by" value={coupon.createdBy} />
                                <FieldRow label="Updated by" value={coupon.updatedBy} />
                                <FieldRow label="Created" value={formatDateTime(coupon.createdAt)} />
                            </DetailCard>

                            {coupon.eligibility && Object.values(coupon.eligibility).some((v) => v !== undefined && v !== false) ? (
                                <DetailCard title="Eligibility rules">
                                    <FieldRow label="First booking only" value={coupon.eligibility.newCustomerOnly ? "Yes" : "No"} />
                                    <FieldRow label="Min completed bookings" value={coupon.eligibility.minCompletedBookings} />
                                    <FieldRow label="Max completed bookings" value={coupon.eligibility.maxCompletedBookings} />
                                    <FieldRow label="Max damage incidents" value={coupon.eligibility.maxDamageIncidents} />
                                    <FieldRow label="Min booking value" value={coupon.eligibility.minBookingValue !== undefined ? currencyFormatter(coupon.eligibility.minBookingValue) : undefined} />
                                    <FieldRow label="Max booking value" value={coupon.eligibility.maxBookingValue !== undefined ? currencyFormatter(coupon.eligibility.maxBookingValue) : undefined} />
                                </DetailCard>
                            ) : ""}
                        </div>

                        <div className="col-12 col-xl-5">
                            <Timeline userLog={logs} />
                        </div>
                    </div>
                ) : ""}

                {tab === "grants" ? (
                    <div className="card border-0 shadow-sm rounded-4">
                        {coupon.audience !== "targeted" ? (
                            <div className="p-4 text-muted small">This coupon's audience is "Everyone" - grants only apply to targeted coupons.</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Customer</th>
                                            <th>Status</th>
                                            <th>Expires</th>
                                            <th>Granted</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {grantsLoading && <tr><td colSpan={5} className="text-center text-muted py-4">Loading...</td></tr>}
                                        {!grantsLoading && grants.list.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">No grants yet.</td></tr>}
                                        {!grantsLoading && grants.list.map((g) => (
                                            <tr key={g.grantId}>
                                                <td role="button" className="text-primary" onClick={() => navigate(`/customers/${g.customerId}`)}>{g.customerId}</td>
                                                <td><span className={`badge rounded-pill ${GRANT_STATUS_BADGE[g.status] || "text-bg-secondary"}`}>{textCapitalize(g.status)}</span></td>
                                                <td>{g.expiresAt ? formatDate(g.expiresAt) : "-"}</td>
                                                <td>{formatDateTime(g.createdAt)}</td>
                                                <td className="text-end">
                                                    {g.status === "active" ? (
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => revokeGrant(g.grantId)}>Revoke</button>
                                                    ) : ""}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="d-flex justify-content-between p-3">
                            <button className="btn btn-outline-secondary btn-sm" disabled={grantsPage === 1 || grantsLoading} onClick={() => setGrantsPage((p) => p - 1)}>Previous</button>
                            <span className="text-muted small">Page {grantsPage}</span>
                            <button className="btn btn-outline-secondary btn-sm" disabled={grants.list.length < 10 || grantsLoading} onClick={() => setGrantsPage((p) => p + 1)}>Next</button>
                        </div>
                    </div>
                ) : ""}

                {tab === "redemptions" ? (
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Booking</th>
                                        <th>Customer</th>
                                        <th>Status</th>
                                        <th className="text-end">Discount</th>
                                        <th>Reserved</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {redemptionsLoading && <tr><td colSpan={5} className="text-center text-muted py-4">Loading...</td></tr>}
                                    {!redemptionsLoading && redemptions.list.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">No redemptions yet.</td></tr>}
                                    {!redemptionsLoading && redemptions.list.map((r) => (
                                        <tr key={r.redemptionId}>
                                            <td role="button" className="text-primary" onClick={() => navigate(`/booking/details/${r.bookingId}`)}>{r.bookingId}</td>
                                            <td>{r.customerId}</td>
                                            <td><span className={`badge rounded-pill ${REDEMPTION_STATUS_BADGE[r.status] || "text-bg-secondary"}`}>{textCapitalize(r.status)}</span></td>
                                            <td className="text-end">{currencyFormatter(r.totalDiscount || 0)}</td>
                                            <td>{formatDateTime(r.reservedAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex justify-content-between p-3">
                            <button className="btn btn-outline-secondary btn-sm" disabled={redemptionsPage === 1 || redemptionsLoading} onClick={() => setRedemptionsPage((p) => p - 1)}>Previous</button>
                            <span className="text-muted small">Page {redemptionsPage}</span>
                            <button className="btn btn-outline-secondary btn-sm" disabled={redemptions.list.length < 10 || redemptionsLoading} onClick={() => setRedemptionsPage((p) => p + 1)}>Next</button>
                        </div>
                    </div>
                ) : ""}
            </div>

            {showEditModal ? (
                <CouponFormModal
                    coupon={coupon}
                    onClose={() => setShowEditModal(false)}
                    onSaved={() => { setShowEditModal(false); load(); }}
                />
            ) : ""}

            {showGrantModal ? (
                <GrantModal
                    couponCode={coupon.couponCode}
                    onClose={() => setShowGrantModal(false)}
                    onGranted={() => { setShowGrantModal(false); if (tab === "grants") loadGrants(); }}
                />
            ) : ""}
        </>
    );
};

export default CouponView;
